import { Avatar } from "@package/subframe/components/Avatar";
import { Button } from "@package/subframe/components/Button";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherArrowLeft,
  FeatherCheck,
  FeatherEye,
  FeatherEyeOff,
} from "@subframe/core";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { useUpdatePasswordWithTokenCommandControllerUpdatePasswordWithToken } from "@/generated/auth/auth";

export const Route = createFileRoute("/_auth/reset-password")({
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    return {
      token: search.token ? String(search.token) : undefined,
    };
  },
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token } = useSearch({ from: "/_auth/reset-password" });
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { mutate: updatePassword, isPending } =
    useUpdatePasswordWithTokenCommandControllerUpdatePasswordWithToken({
      mutation: {
        onSuccess: () => {
          toast.success("Password updated successfully");
          navigate({ to: "/login" });
        },
        onError: (error: Error) => {
          toast.error(error?.message || "Failed to update password");
        },
      },
    });

  const passwordRequirements = [
    { text: "At least 8 characters", met: password.length >= 8 },
    { text: "Contains uppercase letter", met: /[A-Z]/.test(password) },
    { text: "Contains lowercase letter", met: /[a-z]/.test(password) },
    { text: "Contains number", met: /\d/.test(password) },
    {
      text: "Contains special character",
      met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    },
  ];

  const passwordsMatch = password === confirmPassword && password.length > 0;
  const allRequirementsMet = passwordRequirements.every((req) => req.met);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!allRequirementsMet || !passwordsMatch) {
      toast.error("Please meet all password requirements");
      return;
    }

    updatePassword({
      data: {
        email,
        code: token!,
        newPassword: password,
      },
    });
  };

  // Invalid or missing token
  if (!token) {
    return (
      <div className="flex w-full items-center justify-center h-screen">
        {/* Left side - Testimonial */}
        <div className="flex flex-col items-start justify-end gap-6 self-stretch bg-primary-navy-800 pl-24 pr-12 pt-12 pb-32 w-1/2 mobile:hidden">
          <Avatar size="x-large" image="/images/testimonial-avatar.png">
            SK
          </Avatar>
          <div className="flex w-full flex-col items-start gap-6 max-w-[80%]">
            <span className="font-['Manrope'] text-[36px] font-[500] leading-[40px] tracking-wide text-white">
              &quot;This platform has transformed how we manage compliance at
              our nursery. The interface is intuitive and saves us countless
              hours every week.&quot;
            </span>
            <div className="flex flex-wrap items-end gap-2">
              <span className="font-['Manrope'] text-[20px] font-[700] leading-[24px] tracking-tight text-primary-rose">
                Sarah Kennedy
              </span>
              <span className="text-body font-body text-primary-rose">–</span>
              <span className="text-body font-body text-primary-rose">
                Director, Little Sprouts Nursery
              </span>
            </div>
          </div>
        </div>

        {/* Right side - Error */}
        <div className="flex flex-col items-center justify-center self-stretch bg-white px-12 py-12 relative w-1/2 mobile:h-auto mobile:grow mobile:shrink-0 mobile:basis-0 mobile:self-stretch mobile:px-6 mobile:py-6">
          <div className="flex w-full max-w-[384px] flex-col items-start gap-6">
            <div className="flex items-center gap-2 mobile:absolute mobile:left-8 mobile:top-8">
              <img
                className="h-8 flex-none object-cover"
                src="/images/logo.svg"
                alt="Logo"
              />
            </div>

            <span className="text-heading-1 font-heading-1 text-neutral-900">
              Invalid reset link
            </span>

            <span className="text-body font-body text-subtext-color">
              The password reset link is invalid or has expired.
            </span>

            <Link to="/forgot-password" className="w-full">
              <Button
                className="h-12 w-full flex-none"
                variant="brand-primary"
                size="large"
              >
                Request a new reset link
              </Button>
            </Link>

            <div className="flex w-full items-center justify-center">
              <Link to="/login">
                <LinkButton
                  variant="brand"
                  size="small"
                  icon={<FeatherArrowLeft />}
                >
                  Back to sign in
                </LinkButton>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-center h-screen">
      {/* Left side - Testimonial */}
      <div className="flex flex-col items-start justify-end gap-6 self-stretch bg-primary-navy-800 pl-24 pr-12 pt-12 pb-32 w-1/2 mobile:hidden">
        <Avatar size="x-large" image="/images/testimonial-avatar.png">
          SK
        </Avatar>
        <div className="flex w-full flex-col items-start gap-6 max-w-[80%]">
          <span className="font-['Manrope'] text-[36px] font-[500] leading-[40px] tracking-wide text-white">
            &quot;This platform has transformed how we manage compliance at our
            nursery. The interface is intuitive and saves us countless hours
            every week.&quot;
          </span>
          <div className="flex flex-wrap items-end gap-2">
            <span className="font-['Manrope'] text-[20px] font-[700] leading-[24px] tracking-tight text-primary-rose">
              Sarah Kennedy
            </span>
            <span className="text-body font-body text-primary-rose">–</span>
            <span className="text-body font-body text-primary-rose">
              Director, Little Sprouts Nursery
            </span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex flex-col items-center justify-center self-stretch bg-white px-12 py-12 relative w-1/2 mobile:h-auto mobile:grow mobile:shrink-0 mobile:basis-0 mobile:self-stretch mobile:px-6 mobile:py-6">
        <div className="flex w-full max-w-[384px] flex-col items-start gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2 mobile:absolute mobile:left-8 mobile:top-8">
            <img
              className="h-8 flex-none object-cover"
              src="/images/logo.svg"
              alt="Logo"
            />
          </div>

          <span className="text-heading-1 font-heading-1 text-neutral-900">
            Set new password
          </span>

          <span className="text-body font-body text-subtext-color">
            Enter your new password below.
          </span>

          <form
            onSubmit={handleSubmit}
            className="flex w-full flex-col items-start gap-6"
          >
            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="Email"
            >
              <TextField.Input
                placeholder="Enter your email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={isPending}
              />
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="New Password"
              iconRight={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="bg-transparent border-none cursor-pointer p-0"
                >
                  {showPassword ? <FeatherEye /> : <FeatherEyeOff />}
                </button>
              }
            >
              <TextField.Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                disabled={isPending}
              />
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="Confirm Password"
              error={confirmPassword.length > 0 && !passwordsMatch}
              helpText={
                confirmPassword.length > 0 && !passwordsMatch
                  ? "Passwords do not match"
                  : ""
              }
              iconRight={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="bg-transparent border-none cursor-pointer p-0"
                >
                  {showConfirmPassword ? <FeatherEye /> : <FeatherEyeOff />}
                </button>
              }
            >
              <TextField.Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                disabled={isPending}
              />
            </TextField>

            {password && (
              <div className="flex w-full flex-col gap-2">
                <span className="text-caption-bold font-caption-bold text-default-font">
                  Password requirements:
                </span>
                <div className="flex flex-col gap-1">
                  {passwordRequirements.map((req, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-2 text-caption font-caption ${
                        req.met ? "text-success-600" : "text-subtext-color"
                      }`}
                    >
                      {req.met ? (
                        <FeatherCheck className="h-3 w-3" />
                      ) : (
                        <div className="h-3 w-3 rounded-full border border-neutral-300" />
                      )}
                      {req.text}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              className="h-12 w-full flex-none"
              variant="brand-primary"
              size="large"
              type="submit"
              disabled={
                !email || !allRequirementsMet || !passwordsMatch || isPending
              }
              loading={isPending}
            >
              Reset password
            </Button>
          </form>

          <div className="flex w-full items-center justify-center">
            <Link to="/login">
              <LinkButton
                variant="brand"
                size="small"
                icon={<FeatherArrowLeft />}
              >
                Back to sign in
              </LinkButton>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
